import { useEffect, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { useMutation } from "@apollo/client";
import { useFormik } from "formik";
import * as Yup from "yup";
import { CONTACT_FORM_SUBMIT } from "../../graphql/mutations";
import styles from "./ProductsPageClient.module.scss";

const contactSchema = Yup.object().shape({
    subject: Yup.string()
        .trim()
        .required("Required")
        .max(150, "Max 150 characters"),
    message: Yup.string()
        .trim()
        .required("Required")
        .min(20, "Please write a longer message")
        .max(4000, "Max 4000 characters")
});

type SubmitStatus = "idle" | "success" | "error";

export const ContactPage = () => {
    const [status, setStatus] = useState<SubmitStatus>("idle");
    const [contactFormSubmit] = useMutation(CONTACT_FORM_SUBMIT);

    useEffect(() => {
        if (status === "idle") return;
        const t = setTimeout(() => setStatus("idle"), 4000);
        return () => clearTimeout(t);
    }, [status]);

    const formik = useFormik({
        initialValues: { subject: "", message: "" },
        validationSchema: contactSchema,
        onSubmit: async (values, { resetForm }) => {
            try {
                await contactFormSubmit({
                    variables: {
                        subject: values.subject.trim(),
                        message: values.message.trim()
                    }
                });
                resetForm();
                setStatus("success");
            } catch {
                setStatus("error");
            }
        }
    });

    return (
        <Container className="p-4 pageMaxWidthClient">
            <Row className="justify-content-center">
                <Col xs={12} md={8} lg={6} className="mt-1">
                    <h2 className={styles.listTitle}>Contact</h2>
                    <p className="bread-text text-subtle">
                        Feel free to reach out with any questions or feedback!
                        <br />
                        We'll get back to you as soon as possible.
                    </p>
                </Col>
            </Row>
            <Row className="mt-2 justify-content-center">
                <Col xs={12} md={8} lg={6}>
                    <Form onSubmit={formik.handleSubmit}>
                        <Form.Group className="mb-3" controlId="contactSubject">
                            <Form.Label className="text-subtle">
                                Subject
                            </Form.Label>
                            <Form.Control
                                type="text"
                                name="subject"
                                value={formik.values.subject}
                                onChange={e => {
                                    if (status !== "idle") setStatus("idle");
                                    formik.handleChange(e);
                                }}
                                onBlur={formik.handleBlur}
                                isInvalid={Boolean(
                                    formik.touched.subject &&
                                        formik.errors.subject
                                )}
                                onKeyDown={e => {
                                    if (e.key === "Enter") e.preventDefault();
                                }}
                            />
                            <Form.Control.Feedback type="invalid">
                                {formik.errors.subject}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="contactMessage">
                            <Form.Label className="text-subtle text-header">
                                Message
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={6}
                                name="message"
                                value={formik.values.message}
                                onChange={e => {
                                    if (status !== "idle") setStatus("idle");
                                    formik.handleChange(e);
                                }}
                                onBlur={formik.handleBlur}
                                isInvalid={Boolean(
                                    formik.touched.message &&
                                        formik.errors.message
                                )}
                            />
                            <Form.Control.Feedback type="invalid">
                                {formik.errors.message}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Button
                            className="mt-3"
                            type="submit"
                            variant="primary"
                            disabled={
                                !(formik.dirty && formik.isValid) ||
                                formik.isSubmitting
                            }
                        >
                            Send Message
                        </Button>
                        {status === "success" && (
                            <p className="text-success mt-3 mb-0">
                                Message sent! We'll get back to you as soon as
                                possible.
                            </p>
                        )}
                        {status === "error" && (
                            <p className="text-danger mt-3 mb-0">
                                Something went wrong. Please try again.
                            </p>
                        )}
                    </Form>
                </Col>
            </Row>
        </Container>
    );
};
